import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import type {
  AIUsageAgenticWorkflowEntity,
  AIUsageColumn,
  AIUsageEmployeeEntity,
} from '@kizen/api/billing';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import useCan from 'hooks/useCan';
import { isServiceAccount, mapUsageToColumns } from './utils';

const NAME_COLUMN_ID = 'name';
const NAME_COLUMN_WIDTH = '320px';

// Every usage column shares one width, sized so the longest title
// ("Anthropic (via Kizen)" plus its sort arrow, ~151px) stays on one line.
const USAGE_COLUMN_WIDTH_PX = 170;
const USAGE_COLUMN_PADDING_PX = 15;

const SPACER_COLUMN_ID = 'usage-spacer';

const SpacerCell = styled(SizedCell)`
  max-width: none;
`;

type ColumnConfig = {
  id: string;
  headCell: JSX.Element;
  cell: JSX.Element;
  format?: (value: unknown, data: any) => JSX.Element;
};

// Soaks up leftover width so the usage columns keep their fixed size; it
// shrinks to nothing on its own once the columns overflow and scroll.
const spacerColumn: ColumnConfig = {
  id: SPACER_COLUMN_ID,
  headCell: <SpacerCell />,
  cell: <SpacerCell />,
};

const buildUsageColumns = (
  columns: AIUsageColumn[],
  hasSorting: boolean
): ColumnConfig[] =>
  columns.map((column) => ({
    id: column.sort_key,
    headCell: (
      <SortableHeadCell
        hasSorting={hasSorting}
        numeric
        align="right"
        label={column.label}
        width={`${USAGE_COLUMN_WIDTH_PX}px`}
        padding={`${USAGE_COLUMN_PADDING_PX}px`}
      />
    ),
    cell: (
      <SizedCell
        flex
        align="right"
        width={`${USAGE_COLUMN_WIDTH_PX}px`}
        padding={`${USAGE_COLUMN_PADDING_PX}px`}
      />
    ),
    format: (_: unknown, data: any) => {
      const mapped = mapUsageToColumns(data.usage, columns).find(
        (entry) => entry.column.key === column.key
      );

      return (
        <TextEllipsisWithTooltip weight={data.isTotal ? 'bold' : undefined}>
          {mapped?.formatted}
        </TextEllipsisWithTooltip>
      );
    },
  }));

export const useAreaColumns = (columns: AIUsageColumn[]): ColumnConfig[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        id: NAME_COLUMN_ID,
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Usage by Area')}
            width={NAME_COLUMN_WIDTH}
            padding="15px"
          />
        ),
        cell: <SizedCell width={NAME_COLUMN_WIDTH} padding="15px" />,
        format: (_: unknown, data: any) => (
          <TextEllipsisWithTooltip weight={data.isTotal ? 'bold' : undefined}>
            {data.label}
          </TextEllipsisWithTooltip>
        ),
      },
      ...buildUsageColumns(columns, false),
      spacerColumn,
    ],
    [columns, t]
  );
};

const AgenticWorkflowNameCell = ({
  agenticWorkflow,
}: {
  agenticWorkflow: AIUsageAgenticWorkflowEntity;
}) => {
  const { t } = useTranslation();
  const canViewAgenticWorkflows = useCan({
    view: 'sections',
    for: 'automations_section',
  });
  const label = agenticWorkflow.archived
    ? `${agenticWorkflow.display_name} ${t('[archived]')}`
    : agenticWorkflow.display_name;

  if (!agenticWorkflow.archived && canViewAgenticWorkflows) {
    return (
      <TextEllipsisWithTooltip
        type="link"
        to={`/agentic-workflow/${agenticWorkflow.id}`}
        target="_blank"
      >
        {label}
      </TextEllipsisWithTooltip>
    );
  }

  return <TextEllipsisWithTooltip>{label}</TextEllipsisWithTooltip>;
};

const EmployeeNameCell = ({
  employee,
}: {
  employee: AIUsageEmployeeEntity;
}) => {
  const { t } = useTranslation();
  const label = [
    employee.display_name,
    employee.archived ? t('[archived]') : null,
    isServiceAccount(employee.account_type) ? t('[Service Account]') : null,
  ]
    .filter(Boolean)
    .join(' ');

  return <TextEllipsisWithTooltip>{label}</TextEllipsisWithTooltip>;
};

// Workflows/Users breakdown tables: sortable dynamic columns + a name column
// whose renderer differs per bucket (link + [archived] vs. [archived]/[Service Account]).
export const useBreakdownColumns = (
  bucket: 'agentic-workflows' | 'team-members',
  columns: AIUsageColumn[]
): ColumnConfig[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    const nameColumn: ColumnConfig = {
      id: NAME_COLUMN_ID,
      headCell: (
        <SortableHeadCell
          label={
            bucket === 'agentic-workflows'
              ? t('Agentic Workflow Name')
              : t('Team Member / Service Account Name')
          }
          width={NAME_COLUMN_WIDTH}
          padding="15px"
        />
      ),
      cell: <SizedCell width={NAME_COLUMN_WIDTH} padding="15px" />,
      format: (_: unknown, data: any) =>
        bucket === 'agentic-workflows' ? (
          <AgenticWorkflowNameCell agenticWorkflow={data.agentic_workflow} />
        ) : (
          <EmployeeNameCell employee={data.employee} />
        ),
    };

    return [nameColumn, ...buildUsageColumns(columns, true), spacerColumn];
  }, [bucket, columns, t]);
};
